export interface TourStep {
  id: string
  /** Route pattern this step lives on. Use ':id' for dynamic segments. */
  route: string
  /** CSS selector for the element to spotlight. */
  target: string
  /** Where to place the tooltip relative to the target. */
  placement: 'top' | 'bottom' | 'left' | 'right' | 'auto'
  title: string
  body: string
  /** Extra space around the spotlight cutout (default 8px). */
  spotlightPadding?: number
  /** If true, the provider polls with rAF until the element appears. */
  waitForElement?: boolean
}

export const TOUR_STEPS: TourStep[] = [
  {
    id: 'new-decision',
    route: '/app',
    target: '[data-testid="new-decision-btn"]',
    placement: 'bottom',
    title: 'Compare choices with math, not gut',
    body: 'LifeOdds runs thousands of random scenarios across each option — salaries, costs, risks — and tells you which comes out ahead and by how much. Click here to model your first decision.',
  },
  {
    id: 'decision-name',
    route: '/decision/:id',
    target: '[data-testid="decision-name-input"]',
    placement: 'bottom',
    title: 'Name it, then set the time horizon',
    body: 'A clear name forces you to state the real question. The year selector next to it controls how far ahead to calculate — a €50 K gain in year 10 is worth less than €50 K today. Shorter horizons favour early payoffs; longer ones reward patience.',
    waitForElement: true,
  },
  {
    id: 'add-option',
    route: '/decision/:id',
    target: '[data-testid="add-option-btn"]',
    placement: 'bottom',
    title: 'Add each path you\'re weighing',
    body: 'Each option gets its own financial profile — recurring income, costs, one-off payments. Add 2–5 options, then add numbers to each. Rough ranges beat precise figures you\'ll agonise over. Use "AI Suggest" inside an option if you want a starting point.',
    waitForElement: true,
  },
  {
    id: 'run',
    route: '/decision/:id',
    target: '[data-testid="run-section"]',
    placement: 'top',
    title: 'Simulations, risk tolerance, precision',
    body: 'The engine draws thousands of random outcomes from your ranges and ranks the options. The Risk slider matters: slide toward Cautious to favour the option with a better worst-case floor — useful when a bad result would be hard to recover from. More simulations = more stable numbers, slower run.',
    spotlightPadding: 16,
    waitForElement: true,
  },
  {
    id: 'results',
    route: '/results/:id',
    target: '[data-testid="winner-banner"]',
    placement: 'bottom',
    title: 'Reading the result',
    body: 'Win probability = how often this option beat all others across every simulated scenario. Above 65% is a clear lean. Below 55% means the choice is genuinely close — scroll down to see where the uncertainty comes from, then tweak and re-run.',
    waitForElement: true,
  },
]

/** Does the current pathname match a step route pattern? */
export function routeMatches(pathname: string, pattern: string): boolean {
  if (pattern === pathname) return true
  if (pattern.includes(':')) {
    // '/decision/:id' → prefix = '/decision'
    const prefix = pattern.split('/:')[0]
    return pathname.startsWith(prefix + '/')
  }
  return false
}
